/**
 * WriterName — renders an author/writer name. Becomes a clickable
 * pill that opens the WriterStatsDrawer when the viewer is a King or
 * High King. Regular users see the plain text.
 *
 * Feb 12 2026 user spec: *"Wire it such that writer names are
 * clickable by Kings and the High King to show the stats of said
 * Writers, books written, series written, how many episodes, how
 * many readers, the retention, their achievements reading and
 * writing. Full analytics"*
 *
 * Props:
 *   userId   (string)   — the scribe's user_id  (required for click)
 *   name     (string)   — the display name
 *   className (string?) — extra classes for the surrounding text
 *   stopPropagation (bool?) — if true, click won't bubble (use inside cards)
 *   onPlainClick (func?) — optional action for regular-user text, used when
 *     a writer label needs to remain a valid sibling of another control
 */
import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { checkAuth } from "../utils/authUtils";
import WriterStatsDrawer from "./WriterStatsDrawer";

// Tiny module-level cache so this hook only runs once per session.
let _roleCache = null;
let _roleCachePromise = null;
const getViewerRole = async () => {
  if (_roleCache !== null) return _roleCache;
  if (_roleCachePromise) return _roleCachePromise;
  _roleCachePromise = (async () => {
    try {
      const u = await checkAuth();
      _roleCache = (u && u.role) || "";
    } catch {
      _roleCache = "";
    }
    return _roleCache;
  })();
  return _roleCachePromise;
};

const WriterName = ({
  userId,
  name,
  className = "",
  stopPropagation = false,
  onPlainClick,
  testid,
}) => {
  const [role, setRole] = useState(_roleCache);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (_roleCache !== null) return;
    (async () => setRole(await getViewerRole()))();
  }, []);

  const display = name || "Unknown";
  const canClick = userId && ["king", "admin", "coadmin"].includes(role || "");

  if (!canClick) {
    if (onPlainClick) {
      return (
        <button
          type="button"
          onClick={onPlainClick}
          className={`rounded bg-transparent p-0 text-left font-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 ${className}`}
          data-testid={testid}
        >
          {display}
        </button>
      );
    }
    return (
      <span className={className} data-testid={testid}>
        {display}
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(ev) => {
          if (stopPropagation) {
            ev.preventDefault();
            ev.stopPropagation();
          }
          setOpen(true);
        }}
        className={`inline-flex items-center gap-0.5 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/40 rounded ${className}`}
        data-testid={testid || `writer-name-link-${userId}`}
        title={`View ${display}'s writer analytics`}
      >
        {display}
        <ChevronRight className="w-3 h-3 opacity-60 -ml-0.5" />
      </button>
      <WriterStatsDrawer
        userId={userId}
        open={open}
        onClose={() => setOpen(false)}
        displayName={display}
      />
    </>
  );
};

export default WriterName;
